import { Component, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material';
import { BaseComponent } from '../../../../common/components/base.component';
import { ModalService } from '../../../../common/services/modal.service';
import { AccountApiService } from '../../../../services/account.api.service';
import { AlertifyService } from '../../../../services/alertify.service';
import { TravelBuddySettingsModalData } from './travel.buddy.settings.modal.provider';
import { ButtonTriggerEvent } from '../../../../common/components';
import { toKeyValueArray } from '../../../../common/extensions';
import { ScriptService } from '../../../../services/script.service';
import {
  ConfirmModalActions,
  ConfirmModalData
} from "../../../../common/components/confirm.modal/confirm.modal.provider";
import { ConfirmModalComponent } from "../../../../common/components/confirm.modal/confirm.modal.component";

@Component({
  templateUrl: './travel.buddy.settings.modal.component.html',
  styleUrls: ['./travel.buddy.settings.modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TravelBuddySettingsModalComponent extends BaseComponent {
  public loading = false;
  public loadingReport = false;
  public loadingTripPlannerSettings = false;
  public selectedTabIndex = 0;
  public reports;
  public tripPlannerSettings = {
    openAIKey: '',
  };

  public newCity: any = null;

  public settings: {
    isActive: boolean;
    version: string;
    showOnAllClientWidgets: boolean;
    nearbyCities: Array<{
      id: string;
      city: string;
      state: string;
      lat: number;
      lng: number;
      placeId: string;
    }>;
    openAIAssistant: {
      id: string;
      vectorStoreId: string;
      files: {
        name: string;
        fileType: 'ImGoing-events' | 'ImGoing-places' | 'client-uploaded' | 'link-content';
        openAIFileId: string;
        size: number;
        uploadedAt: Date;
        link?: string;
      }[];
    };
    cityName: string;
    openAIKey: string;
    clientPrompt: string;
    isStandalone: boolean;
  } = {
    isActive: false,
    version: 'old',
    showOnAllClientWidgets: false,
    nearbyCities: [],
    openAIAssistant: {
      id: '',
      vectorStoreId: '',
      files: [],
    },
    cityName: '',
    openAIKey: '',
    clientPrompt: '',
    isStandalone: false,
  };

  public account: TravelBuddySettingsModalData;
  public formGroup: FormGroup;
  public tripPlannerForm: FormGroup;
  public isNewVersion = false;

  public chart: any;

  constructor(
    private fb: FormBuilder,
    private accountApiService: AccountApiService,
    private alertify: AlertifyService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private renderer: Renderer2,
    private dialogRef: MatDialogRef<TravelBuddySettingsModalComponent>,
    data: TravelBuddySettingsModalData
  ) {
    super();
    this.account = Object.assign({}, data);
  }

  ngOnInit() {
    this.formGroup = this.formBuilder.group({
      assistantId: [null, [Validators.required]],
      cityName: [null, [Validators.required]],
      apikey: [null, [Validators.required]],
    });

    this.tripPlannerForm = this.formBuilder.group({
      openAIKey: [null, [Validators.required]],
    });

    this.loading = true;
    this.accountApiService
      .getTravelBuddySettings(this.account.account.userName)
      .finally(() => (this.loading = false))
      .subscribe((settings) => {
        if (settings) {
          this.settings = settings;
          this.isNewVersion = this.settings.version === 'new';
        }
      });
  }

  close() {
    this.dialogRef.close();
  }

  toggleTravelBuddy() {
    this.accountApiService
      .setTravelBuddySettings(this.account.account.userName, this.settings)
      .finally(() => (this.loading = false))
      .subscribe((version) => {
        this.alertify.successfully('Set travel buddy settings');
      });
  }

  toggleVersion() {
    this.settings.version = this.settings.version === 'old' ? 'new' : 'old';
    this.isNewVersion = this.settings.version === 'new';
    this.toggleTravelBuddy();
  }

  addNearbyCity() {
    console.log(this.newCity);
    if (this.newCity) {
      if (this.settings.nearbyCities.some((x) => x.placeId === this.newCity.placeId)) {
        this.alertify.warning('City already added');
        return;
      }
      this.settings.nearbyCities.push(this.newCity);
      this.newCity = null;
    }
  }

  removeNearbyCity(city) {
    this.settings.nearbyCities = this.settings.nearbyCities.filter(
      (x) => x.placeId !== city.placeId
    );
  }

  saveNewSettings(event: ButtonTriggerEvent) {
    this.accountApiService
      .setTravelBuddySettings(this.account.account.userName, this.settings)
      .finally(() => {
        this.loading = false;
        event.complete();
      })
      .subscribe((version) => {
        this.alertify.successfully('Set travel buddy settings');
      });
  }

  createAssistant(event: ButtonTriggerEvent) {
    this.accountApiService
      .createOpenAIAssistant(
        this.account.account.userName,
        this.settings.cityName,
        this.settings.openAIKey
      )
      .finally(event.complete)
      .subscribe((data) => {
        if (data.success) {
          this.settings.openAIAssistant.id = data.assistant.id;
          this.alertify.successfully('Created assistant');
        } else {
          this.alertify.error(data.error.message);
        }
      });
  }

  uploadFiles(event: ButtonTriggerEvent) {
    this.accountApiService
      .uploadTravelBuddyFiles(this.account.account.userName)
      .finally(event.complete)
      .subscribe((data) => {
        console.log(data);
        this.alertify.successfully('Uploaded files');
        this.settings.openAIAssistant.files = data;
      });
  }

  deleteAssistant() {
    let actions = <ConfirmModalActions>{
      confirm: (feed: ButtonTriggerEvent) => {
        this.accountApiService
          .deleteAssistant(this.account.account.userName)
          .finally(feed.complete)
          .subscribe(() => {
            this.alertify.success(
              `Delete assistant successfully. Please also delete the OpenAI key and archive the project on OpenAI website.`,
              15
            );
            this.close();
          });
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription:
        'Are you sure you want to delete OpenAI Assistant for this client, this operation cannot be reverted?',
      confirmModalConfirmButtonText: 'Delete',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  confirm(event: ButtonTriggerEvent) {
    this.accountApiService
      .setTravelBuddySettings(this.account.account.userName, this.settings)
      .finally(() => (this.loading = false))
      .subscribe((version) => {
        this.alertify.successfully('Set travel buddy settings');
        this.close();
      });
  }

  getEventFileUploadTime() {
    if (this.settings && this.settings.openAIAssistant && this.settings.openAIAssistant.files) {
      const eventFile = this.settings.openAIAssistant.files.find(
        (x) => x.fileType === 'ImGoing-events'
      );
      if (eventFile && eventFile.uploadedAt) {
        return new Date(eventFile.uploadedAt).toLocaleString();
      }
    }
    return 'N/A';
  }

  getPlaceFileUploadTime() {
    if (this.settings && this.settings.openAIAssistant && this.settings.openAIAssistant.files) {
      const placeFile = this.settings.openAIAssistant.files.find(
        (x) => x.fileType === 'ImGoing-places'
      );
      if (placeFile && placeFile.uploadedAt) {
        return new Date(placeFile.uploadedAt).toLocaleString();
      }
    }
    return 'N/A';
  }

  changeTab($event) {
    this.selectedTabIndex = $event;

    if (this.selectedTabIndex === 1) {
      this.loadingReport = true;

      this.accountApiService
        .getTravelBuddyReport(this.account.account.userName)
        .finally(() => (this.loadingReport = false))
        .subscribe((data) => {
          console.log(data);

          this.reports = data;

          console.log('this.chart', this.chart);
          this.chart && this.chart.destroy();

          const chartUrl = `https://cdn.jsdelivr.net/npm/apexcharts`;
          if (!ScriptService.isScriptExist(chartUrl)) {
            const scriptElement = ScriptService.loadJsScript(this.renderer, chartUrl);
            scriptElement.onload = () => {
              this.loadChart(data);
            };
          } else {
            this.loadChart(data);
          }
        });
    }

    if (this.selectedTabIndex === 2) {
      this.loadingTripPlannerSettings = true;

      this.accountApiService
        .getTripPlannerSettings(this.account.account.userName)
        .finally(() => (this.loadingTripPlannerSettings = false))
        .subscribe((data) => {
          this.tripPlannerSettings = data;
        });
    }
  }

  saveTripPlannerSettings(event: ButtonTriggerEvent) {
    this.accountApiService
      .setTripPlannerSettings(this.account.account.userName, this.tripPlannerSettings)
      .finally(event.complete)
      .subscribe((data) => {
        this.alertify.successfully('Saved trip planner settings');
      });
  }

  getKeys(obj) {
    return Object.keys(obj);
  }

  calculateAverageTimeByMonth(entries) {
    let total = 0;
    this.getKeys(entries).forEach((key) => {
      total += this.calculateAverageTime(entries[key]);
    });
    return parseFloat(
      (total / this.getKeys(entries).filter((x) => !!entries[x]).length).toFixed(1)
    );
  }

  calculateAverageTime(entries): number {
    if (!entries) {
      return 0;
    }
    const total = entries.reduce((sum, entry) => sum + entry.timeConsumed, 0);
    return parseFloat((total / entries.length).toFixed(1));
  }

  loadChart(reportData) {
    let total: number[] = [];
    this.getKeys(reportData)
      .reverse()
      .map((key) => {
        reportData[key].total = reportData[key].length;
        total.push(this.getKeys(reportData[key]).length);
      });

    var options = {
      series: [
        {
          name: 'Activitied users',
          group: 'Facebook',
          data: total, //[44000, 0, 41000, 0, 22000, 43000],
        },
        // {
        //   name: 'Facebook updated',
        //   group: 'Facebook',
        //   data: chartData.facebook.updated, //[48000, 50000, 40000, 65000, 25000, 40000],
        // },
      ],
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
      },
      stroke: {
        width: 1,
        colors: ['#fff'],
      },
      dataLabels: {
        formatter: (val) => {
          // return val / 1000 + 'K';
          return val;
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
        },
      },
      xaxis: {
        categories: this.getKeys(reportData).reverse(), //['2024-3-22', '2024-3-24', '2024-3-25', '2024-3-26', '2024-3-27', '2024-3-28'],
      },
      fill: {
        opacity: 1,
      },
      colors: ['#4267B2'], //['#4267B2', '#4267b2b0', '#d1410c', '#d1410c9c', '#ffce44', '#ffce4499'],
      yaxis: {
        labels: {
          formatter: (val) => {
            // return val / 1000 + 'K';
            return val;
          },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
    };
    this.chart = new window['ApexCharts'](
      document.querySelector('#travel-buddy-report-chart'),
      options
    );
    this.chart && this.chart.render();
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/components/admin/accounts/travel.buddy.settings/travel.buddy.settings.modal.component.ts